import type { NationalityRef, PdaContext, PdaOfflineGrant, PdaQrKey } from '@fuel-system/contracts';
import type { PdaSession } from '@/features/auth/session';
import { inTransaction, openDatabase, requestToPromise, type StoreName } from './idb';
import type {
  CachedCustomer,
  CachedPendingSale,
  DeviceIdentity,
  LocalDelivery,
  OutboxOperation,
  PinRecord,
  RecentOperation,
  SaleTombstone,
} from './types';

/** Keys of the `kv` store. */
interface KvValues {
  device: DeviceIdentity;
  context: PdaContext;
  nationalities: NationalityRef[];
  qrKeys: PdaQrKey[];
  grant: PdaOfflineGrant;
  session: PdaSession;
  cursor: string;
  /** The latest server time seen in a pull: an earlier device clock was set back. */
  serverTime: string;
}
type KvKey = keyof KvValues;

/**
 * The device data of fuel-pda.md section 13.3 in IndexedDB. Every method is
 * one transaction; a resolved write has committed.
 */
export class OfflineStore {
  readonly db: IDBDatabase;

  constructor(db: IDBDatabase) {
    this.db = db;
  }

  static async open(factory: IDBFactory, name?: string): Promise<OfflineStore> {
    return new OfflineStore(await openDatabase(factory, name));
  }

  close(): void {
    this.db.close();
  }

  // --- key-value -------------------------------------------------------------

  getValue<K extends KvKey>(key: K): Promise<KvValues[K] | undefined> {
    return inTransaction(this.db, 'kv', 'readonly', (tx) =>
      requestToPromise(tx.objectStore('kv').get(key) as IDBRequest<KvValues[K] | undefined>),
    );
  }

  setValue<K extends KvKey>(key: K, value: KvValues[K]): Promise<void> {
    return inTransaction(this.db, 'kv', 'readwrite', async (tx) => {
      await requestToPromise(tx.objectStore('kv').put(value, key));
    });
  }

  deleteValue(key: KvKey): Promise<void> {
    return inTransaction(this.db, 'kv', 'readwrite', async (tx) => {
      await requestToPromise(tx.objectStore('kv').delete(key));
    });
  }

  /**
   * The install identity, created once with `makeCode` (section 13.5). Read
   * and created in one transaction, so two tabs never create two codes.
   */
  device(makeCode: () => string, makeId: () => string): Promise<DeviceIdentity> {
    return inTransaction(this.db, 'kv', 'readwrite', async (tx) => {
      const kv = tx.objectStore('kv');
      const existing = (await requestToPromise(kv.get('device'))) as DeviceIdentity | undefined;
      if (existing) return existing;
      const created: DeviceIdentity = { id: makeId(), code: makeCode(), seq: 0 };
      await requestToPromise(kv.put(created, 'device'));
      return created;
    });
  }

  /**
   * Hands out the next sequence number and stores `build(seq)` in the outbox
   * and the recent list in the same transaction: a sequence number is never
   * used twice and an operation is never half saved.
   */
  enqueue(
    build: (
      seq: number,
      device: DeviceIdentity,
    ) => {
      operation: OutboxOperation;
      recent: RecentOperation;
      delivered?: LocalDelivery;
    },
  ): Promise<{ operation: OutboxOperation; recent: RecentOperation }> {
    return inTransaction(
      this.db,
      ['kv', 'outbox', 'recent', 'delivered'],
      'readwrite',
      async (tx) => {
        const kv = tx.objectStore('kv');
        const device = (await requestToPromise(kv.get('device'))) as DeviceIdentity | undefined;
        if (!device) throw new Error('The device identity is missing');
        const seq = device.seq + 1;
        const built = build(seq, device);
        await requestToPromise(kv.put({ ...device, seq }, 'device'));
        await requestToPromise(tx.objectStore('outbox').add(built.operation));
        await requestToPromise(tx.objectStore('recent').put(built.recent));
        if (built.delivered)
          await requestToPromise(tx.objectStore('delivered').put(built.delivered));
        return { operation: built.operation, recent: built.recent };
      },
    );
  }

  // --- PIN ---------------------------------------------------------------------

  getPin(userId: string): Promise<PinRecord | undefined> {
    return this.get('pins', userId);
  }

  putPin(record: PinRecord): Promise<void> {
    return this.put('pins', record);
  }

  deletePin(userId: string): Promise<void> {
    return this.delete('pins', userId);
  }

  // --- customers and pending sales ---------------------------------------------

  /** Applies a pull's customer changes; a full pull (`replace`) drops every other row. */
  applyCustomers(items: CachedCustomer[], removedIds: string[], replace: boolean): Promise<void> {
    return this.applyList('customers', items, removedIds, replace);
  }

  async customerByPhone(phone: string): Promise<CachedCustomer | undefined> {
    return inTransaction(this.db, 'customers', 'readonly', async (tx) => {
      const rows = (await requestToPromise(
        tx.objectStore('customers').index('phone').getAll(phone),
      )) as CachedCustomer[];
      return rows[0];
    });
  }

  /**
   * Applies a pull's pending-sales changes. Every removed reference becomes a
   * tombstone, so a signed QR of it is refused offline; a sale listed again
   * loses its tombstone.
   */
  applyPendingSales(
    items: CachedPendingSale[],
    removedReferences: string[],
    replace: boolean,
    recordedAt: string,
  ): Promise<void> {
    return inTransaction(this.db, ['pendingSales', 'tombstones'], 'readwrite', async (tx) => {
      const sales = tx.objectStore('pendingSales');
      const tombstones = tx.objectStore('tombstones');
      if (replace) await requestToPromise(sales.clear());
      for (const reference of removedReferences) {
        await requestToPromise(sales.delete(reference));
        const existing = (await requestToPromise(tombstones.get(reference))) as
          SaleTombstone | undefined;
        // A delivery this device saw keeps its name and time.
        if (existing?.reason !== 'DELIVERED') {
          const tombstone: SaleTombstone = {
            reference,
            reason: 'REMOVED',
            deliveredBy: null,
            deliveredAt: null,
            recordedAt,
          };
          await requestToPromise(tombstones.put(tombstone));
        }
      }
      for (const item of items) {
        await requestToPromise(sales.put(item));
        await requestToPromise(tombstones.delete(item.reference));
      }
    });
  }

  /** A sale delivered online from this device: off the list and refused offline. */
  recordDeliveredOnline(
    reference: string,
    deliveredBy: string,
    deliveredAt: string,
  ): Promise<void> {
    return inTransaction(this.db, ['pendingSales', 'tombstones'], 'readwrite', async (tx) => {
      await requestToPromise(tx.objectStore('pendingSales').delete(reference));
      const tombstone: SaleTombstone = {
        reference,
        reason: 'DELIVERED',
        deliveredBy,
        deliveredAt,
        recordedAt: deliveredAt,
      };
      await requestToPromise(tx.objectStore('tombstones').put(tombstone));
    });
  }

  tombstone(reference: string): Promise<SaleTombstone | undefined> {
    return this.get('tombstones', reference);
  }

  pendingSale(reference: string): Promise<CachedPendingSale | undefined> {
    return this.get('pendingSales', reference);
  }

  removePendingSale(reference: string): Promise<void> {
    return this.delete('pendingSales', reference);
  }

  // --- deliveries made here ------------------------------------------------------

  localDelivery(reference: string): Promise<LocalDelivery | undefined> {
    return this.get('delivered', reference);
  }

  removeLocalDelivery(reference: string): Promise<void> {
    return this.delete('delivered', reference);
  }

  // --- outbox --------------------------------------------------------------------

  /** Every waiting operation, in device order. */
  outbox(): Promise<OutboxOperation[]> {
    return inTransaction(this.db, 'outbox', 'readonly', (tx) =>
      requestToPromise(
        tx.objectStore('outbox').index('seq').getAll() as IDBRequest<OutboxOperation[]>,
      ),
    );
  }

  outboxCount(): Promise<number> {
    return inTransaction(this.db, 'outbox', 'readonly', (tx) =>
      requestToPromise(tx.objectStore('outbox').count()),
    );
  }

  putOutbox(operation: OutboxOperation): Promise<void> {
    return this.put('outbox', operation);
  }

  removeOutbox(id: string): Promise<void> {
    return this.delete('outbox', id);
  }

  // --- recent ---------------------------------------------------------------------

  recent(): Promise<RecentOperation[]> {
    return this.all<RecentOperation>('recent');
  }

  recentById(id: string): Promise<RecentOperation | undefined> {
    return this.get('recent', id);
  }

  /** The entry printed with `reference`: its final or its temporary reference. */
  async recentByReference(reference: string): Promise<RecentOperation | undefined> {
    const wanted = reference.trim().toUpperCase();
    const rows = await this.all<RecentOperation>('recent');
    return rows.find((row) => row.reference === wanted || row.temporaryReference === wanted);
  }

  putRecent(entry: RecentOperation): Promise<void> {
    return this.put('recent', entry);
  }

  /**
   * Keeps synced entries of the kept business dates only. Waiting and failed
   * (CONFLICT / REFUSED) entries always stay, so a refusal never disappears
   * from آخر عملياتي before the employee has seen it.
   */
  async pruneRecent(keepDates: readonly string[]): Promise<void> {
    const keep = new Set(keepDates);
    await inTransaction(this.db, 'recent', 'readwrite', async (tx) => {
      const store = tx.objectStore('recent');
      const rows = (await requestToPromise(store.getAll())) as RecentOperation[];
      for (const row of rows) {
        if (row.syncState === 'SYNCED' && !keep.has(row.businessDate)) {
          await requestToPromise(store.delete(row.id));
        }
      }
    });
  }

  // --- helpers ----------------------------------------------------------------------

  private get<T>(store: StoreName, key: string): Promise<T | undefined> {
    return inTransaction(this.db, store, 'readonly', (tx) =>
      requestToPromise(tx.objectStore(store).get(key) as IDBRequest<T | undefined>),
    );
  }

  private all<T>(store: StoreName): Promise<T[]> {
    return inTransaction(this.db, store, 'readonly', (tx) =>
      requestToPromise(tx.objectStore(store).getAll() as IDBRequest<T[]>),
    );
  }

  private put(store: StoreName, value: unknown): Promise<void> {
    return inTransaction(this.db, store, 'readwrite', async (tx) => {
      await requestToPromise(tx.objectStore(store).put(value));
    });
  }

  private delete(store: StoreName, key: string): Promise<void> {
    return inTransaction(this.db, store, 'readwrite', async (tx) => {
      await requestToPromise(tx.objectStore(store).delete(key));
    });
  }

  private applyList(
    store: StoreName,
    items: object[],
    removed: string[],
    replace: boolean,
  ): Promise<void> {
    return inTransaction(this.db, store, 'readwrite', async (tx) => {
      const objectStore = tx.objectStore(store);
      if (replace) await requestToPromise(objectStore.clear());
      for (const key of removed) await requestToPromise(objectStore.delete(key));
      for (const item of items) await requestToPromise(objectStore.put(item));
    });
  }
}
